import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Empty,
  EmptyDescription,
  Skeleton,
} from '@monksflow/monks-ui';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { downloadFile } from '@/shared/utils/downloadFile';
import { exportLibrary } from '../api/format-specs-admin.api';
import { useFormatLibrary } from '../model/useFormatLibrary';
import { FormatEditor } from './FormatEditor';
import { FormatTree } from './FormatTree';
import { ImportDialog } from './ImportDialog';
import { NewFormatDialog } from './NewFormatDialog';
import { NewGroupDialog } from './NewGroupDialog';

/**
 * The curated ad format library, as a master/detail pair: platform groups and formats on the left,
 * the selected format's editor on the right. The data is global - the notice says so, because the
 * admin layout around it is otherwise scoped to one organisation.
 */
export function FormatSpecsAdmin() {
  const { t } = useTranslation('admin');
  const library = useFormatLibrary();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'new' | 'newGroup' | 'import' | null>(null);
  const [newFormatGroup, setNewFormatGroup] = useState<string | undefined>();
  const [exporting, setExporting] = useState(false);

  const state = library.data?.state;
  useEffect(() => {
    if (state && selectedKey === null && state.formats.length > 0) {
      setSelectedKey(state.formats[0].key);
    }
  }, [state, selectedKey]);

  const exportAll = async () => {
    setExporting(true);
    try {
      await downloadFile({
        blob: await exportLibrary(),
        // eslint-disable-next-line i18next/no-literal-string -- download file name
        fileName: `format-specs-v${library.data?.datasetVersion ?? 'latest'}.zip`,
      });
    } catch {
      notificationService.showError(t('admin.adFormats.errors.generic'));
    } finally {
      setExporting(false);
    }
  };

  if (library.isPending) {
    return (
      <div className="grid grid-cols-[18rem_1fr] gap-xl">
        <Skeleton className="h-96 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!state) {
    return (
      <Empty>
        <EmptyDescription>{t('admin.adFormats.loadFailed')}</EmptyDescription>
        <Button variant="outline" onClick={() => void library.refetch()}>
          {t('admin.adFormats.retry')}
        </Button>
      </Empty>
    );
  }

  const selected = state.formats.find((format) => format.key === selectedKey);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-md">
      <div className="flex flex-wrap items-start gap-sm">
        <Alert variant="default" className="min-w-0 flex-1">
          <AlertDescription>{t('admin.adFormats.globalNotice')}</AlertDescription>
        </Alert>
        <div className="flex flex-wrap gap-xs">
          <Button variant="outline" onClick={() => setDialog('import')}>
            <UploadIcon aria-hidden />
            {t('admin.adFormats.import')}
          </Button>
          <Button variant="outline" disabled={exporting} onClick={() => void exportAll()}>
            <DownloadIcon aria-hidden />
            {t('admin.adFormats.export')}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-xl md:grid-cols-[18rem_1fr]">
        <FormatTree
          state={state}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onNewFormat={(groupKey) => {
            setNewFormatGroup(groupKey);
            setDialog('new');
          }}
          onNewGroup={() => setDialog('newGroup')}
        />
        <div className="min-w-0 overflow-y-auto">
          {selected ? (
            <FormatEditor key={selected.key} state={state} format={selected} />
          ) : (
            <Empty>
              <EmptyDescription>{t('admin.adFormats.selectFormat')}</EmptyDescription>
            </Empty>
          )}
        </div>
      </div>

      <NewFormatDialog
        open={dialog === 'new'}
        groups={state.groups}
        initialGroupKey={newFormatGroup}
        onOpenChange={(open) => setDialog(open ? 'new' : null)}
        onCreated={setSelectedKey}
      />
      <NewGroupDialog
        open={dialog === 'newGroup'}
        onOpenChange={(open) => setDialog(open ? 'newGroup' : null)}
      />
      <ImportDialog
        open={dialog === 'import'}
        onOpenChange={(open) => setDialog(open ? 'import' : null)}
      />
    </div>
  );
}
